import {
    clearError,
    clearSelectedUser,
    selectUser as selectUserAction,
    setError,
    setMigrationSummary,
    setPlanSaving,
    setSelectedUserLabel,
    setUserSearchValue as setUserSearchValueAction,
    startCreatingPlan,
    startEditingPlan,
    stopEditingPlan,
    updatePlanFormField as updatePlanFormFieldAction,
} from './actions';
import type { SubscriptionAdminAction } from './actions';
import {
    createDefaultFormState,
    formStateToReplaceRequest,
    formStateToWriteRequest,
    planToFormState,
} from './formatting';
import type { SubscriptionAdminModel } from './index';
import { reducer, createInitialState } from './reducer';
import type { SubscriptionAdminState } from './reducer';
import type { MigrationSummaryCounts, PlanFormState } from './types';
import type { ReadonlySignal } from '@preact/signals-react';
import { computed, effect, signal } from '@preact/signals-react';
import { MutationObserver } from '@tanstack/query-core';
import type {
    ChangeSubscriptionRequest,
    PaginatedSubscriptionSearchResponse,
    Subscription,
    SubscriptionPlan,
    SubscriptionSearchRow,
    CreateSubscriptionPlanResponse,
    ReplaceSubscriptionPlanRequest,
    ReplaceSubscriptionPlanResponse,
    SubscriptionPlanWriteRequest,
} from '@thinkalpha/access-ws-client/contracts/subscription.js';
import type { PermissionType } from '@thinkalpha/common/contracts/permissions.js';
import type { Group } from '@thinkalpha/ui-types/types/admin/groups';
import type { User } from '@thinkalpha/ui-types/types/admin/users';
import { createDataTablePaging } from 'src/admin/components/data-table/clientPagedTableModel';
import type { UserSearchItem } from 'src/admin/users/components/UserSearch';
import { inject, injectable } from 'src/features/ioc';
import type { ReducerDevtoolsConnection } from 'src/lib/devtools';
import { connectReducerDevtools } from 'src/lib/devtools';
import { formatUser } from 'src/lib/util/formatUser';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getGroupsQuery } from 'src/queries/group';
import { getKnownRolesQuery } from 'src/queries/roles';
import {
    getPlansQuery,
    getCancelSubscriptionMutation,
    getReactivateSubscriptionMutation,
    getChangeSubscriptionMutation,
    getReplacePlanMutation,
    getCreatePlanMutation,
    getUserSubscriptionsQuery,
    searchSubscriptionsQuery,
    invalidateSubscriptionsQuery,
    invalidateUserSubscriptionsQuery,
    invalidatePlansQuery,
} from 'src/queries/subscriptions';
import { getBulkUsers, getSearchUsersQuery } from 'src/queries/user';
import type { ReactBindings } from 'src/types/bindings';

@injectable()
export class SubscriptionAdminModelImpl implements SubscriptionAdminModel {
    readonly #disposableStack = new DisposableStack();

    #state = signal<SubscriptionAdminState>(createInitialState());

    #plansQuery: QueryObserverModel<SubscriptionPlan[], Error, SubscriptionPlan[]>;
    #allSubscriptionsQuery: QueryObserverModel<
        PaginatedSubscriptionSearchResponse,
        Error,
        PaginatedSubscriptionSearchResponse
    >;
    #userSubscriptionsQuery: QueryObserverModel<Subscription[], Error, Subscription[]>;
    #userSearchQuery: QueryObserverModel<User[], Error, User[]>;
    #knownRolesQuery: QueryObserverModel<string[], Error, string[]>;
    #groupsQuery: QueryObserverModel<Group[], Error, Group[]>;

    #cancelSubscriptionMutation: MutationObserver<Subscription, Error, { id: string; immediately?: boolean }>;
    #reactivateSubscriptionMutation: MutationObserver<Subscription, Error, string>;
    #changeSubscriptionMutation: MutationObserver<
        Subscription,
        Error,
        { id: string; request: ChangeSubscriptionRequest }
    >;
    #replacePlanMutation: MutationObserver<
        ReplaceSubscriptionPlanResponse,
        Error,
        { planId: string; request: ReplaceSubscriptionPlanRequest }
    >;
    #createPlanMutation: MutationObserver<CreateSubscriptionPlanResponse, Error, SubscriptionPlanWriteRequest>;

    #devtools: ReducerDevtoolsConnection<SubscriptionAdminState, SubscriptionAdminAction> | undefined;

    #dispatch(action: SubscriptionAdminAction): void {
        this.#state.value = reducer(this.#state.value, action);
        this.#devtools?.send(action, this.#state.value);
    }

    readonly plansPaging = createDataTablePaging();
    readonly subscriptionsPaging = createDataTablePaging();

    // Plans data
    readonly plans: ReadonlySignal<SubscriptionPlan[]> = computed(() => this.#plansQuery?.signal.value.data ?? []);
    readonly loading: ReadonlySignal<boolean> = computed(() => this.#plansQuery?.signal.value.isFetching || false);
    readonly error: ReadonlySignal<string | null> = computed(() => this.#state.value.error);

    // All subscriptions
    readonly allSubscriptions: ReadonlySignal<SubscriptionSearchRow[]> = computed(
        () => this.#allSubscriptionsQuery?.signal.value.data?.results ?? [],
    );
    readonly allSubscriptionsCount: ReadonlySignal<number> = computed(
        () => this.#allSubscriptionsQuery?.signal.value.data?.count ?? 0,
    );
    readonly allSubscriptionsLoading: ReadonlySignal<boolean> = computed(
        () => this.#allSubscriptionsQuery?.signal.value.isFetching || false,
    );

    #userDisplayNames = signal<ReadonlyMap<string, string>>(new Map());
    readonly userDisplayNames: ReadonlySignal<ReadonlyMap<string, string>> = this.#userDisplayNames;

    // User search / lookup
    readonly userSearchValue: ReadonlySignal<string> = computed(() => this.#state.value.userSearchValue);
    readonly selectedUserId: ReadonlySignal<string | null> = computed(() => this.#state.value.selectedUserId);
    readonly selectedUserLabel: ReadonlySignal<string | null> = computed(() => this.#state.value.selectedUserLabel);

    readonly userSearchItems: ReadonlySignal<UserSearchItem[]> = computed(() => {
        const users = this.#userSearchQuery?.signal.value.data;
        const selectedId = this.#state.value.selectedUserId;
        return (
            users?.map((user) => ({
                value: user.id,
                label: formatUser(user),
                isSelected: user.id === selectedId,
            })) ?? []
        );
    });

    readonly commandEmptyText: ReadonlySignal<string> = computed(() => {
        const status = this.#userSearchQuery?.signal.value.status;
        if (status === 'error') return 'There was an error.';
        if (status === 'pending') return 'Loading users...';
        return 'No user found.';
    });

    readonly selectedUserSubscriptions: ReadonlySignal<Subscription[]> = computed(
        () => this.#userSubscriptionsQuery?.signal.value.data ?? [],
    );

    readonly selectedUserLoading: ReadonlySignal<boolean> = computed(
        () => this.#userSubscriptionsQuery?.signal.value.isFetching || false,
    );

    readonly selectedUserError: ReadonlySignal<string | null> = computed(() => {
        const error = this.#userSubscriptionsQuery?.signal.value.error;
        return error instanceof Error ? error.message : error ? String(error) : null;
    });

    // Plan edit/create form
    readonly isCreating: ReadonlySignal<boolean> = computed(() => this.#state.value.isCreating);
    readonly editingPlanId: ReadonlySignal<string | null> = computed(() => this.#state.value.editingPlanId);
    readonly editingPlan: ReadonlySignal<SubscriptionPlan | undefined> = computed(() => {
        const planId = this.#state.value.editingPlanId;
        if (!planId) return undefined;
        return this.plans.value.find((p) => p.id === planId);
    });
    readonly planFormState: ReadonlySignal<PlanFormState | null> = computed(() => this.#state.value.planFormState);
    readonly planSaving: ReadonlySignal<boolean> = computed(() => this.#state.value.planSaving);
    readonly lastMigrationSummary: ReadonlySignal<MigrationSummaryCounts | null> = computed(
        () => this.#state.value.lastMigrationSummary,
    );

    readonly knownRoles: ReadonlySignal<string[]> = computed(() => this.#knownRolesQuery?.signal.value.data ?? []);

    readonly unassignedRoles: ReadonlySignal<string[]> = computed(() => {
        const known = this.knownRoles.value;
        const assigned = this.#state.value.planFormState?.roles ?? [];
        return known.filter((r) => r && !assigned.includes(r)).sort();
    });

    readonly availableGroups: ReadonlySignal<Group[]> = computed(() => this.#groupsQuery?.signal.value.data ?? []);

    constructor(
        @inject('QueryObserverModelFactory')
        private queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('QueryClient')
        private queryClient: ReactBindings['QueryClient'],
    ) {
        // Admin panel needs every plan, including inactive ones (the public listing filters those out).
        this.#plansQuery = this.queryObserverModelFactory<SubscriptionPlan[], Error, SubscriptionPlan[]>(
            getPlansQuery({ includeAll: true }),
        );
        this.#disposableStack.use(this.#plansQuery);

        this.#allSubscriptionsQuery = this.queryObserverModelFactory<
            PaginatedSubscriptionSearchResponse,
            Error,
            PaginatedSubscriptionSearchResponse
        >(searchSubscriptionsQuery({ $top: 100 }));
        this.#disposableStack.use(this.#allSubscriptionsQuery);

        this.#userSubscriptionsQuery = this.queryObserverModelFactory<Subscription[], Error, Subscription[]>(
            computed(() => getUserSubscriptionsQuery(this.#state.value.selectedUserId ?? undefined)),
        );
        this.#disposableStack.use(this.#userSubscriptionsQuery);

        this.#userSearchQuery = this.queryObserverModelFactory<User[], Error, User[]>(
            computed(() => getSearchUsersQuery(this.#state.value.userSearchValue)),
        );
        this.#disposableStack.use(this.#userSearchQuery);

        this.#knownRolesQuery = this.queryObserverModelFactory<string[], Error, string[]>(getKnownRolesQuery());
        this.#disposableStack.use(this.#knownRolesQuery);

        this.#groupsQuery = this.queryObserverModelFactory<Group[], Error, Group[]>(getGroupsQuery());
        this.#disposableStack.use(this.#groupsQuery);

        this.#cancelSubscriptionMutation = new MutationObserver(this.queryClient, getCancelSubscriptionMutation());
        this.#reactivateSubscriptionMutation = new MutationObserver(
            this.queryClient,
            getReactivateSubscriptionMutation(),
        );
        this.#changeSubscriptionMutation = new MutationObserver(this.queryClient, getChangeSubscriptionMutation());
        this.#replacePlanMutation = new MutationObserver(this.queryClient, getReplacePlanMutation());
        this.#createPlanMutation = new MutationObserver(this.queryClient, getCreatePlanMutation());

        this.#devtools = connectReducerDevtools({ name: 'SubscriptionAdmin' });
        if (this.#devtools) {
            this.#disposableStack.use(this.#devtools);
            this.#devtools.init(this.#state.value);
        }

        // Resolve user display names when subscriptions load (all or selected user)
        const resolveUserNames = (subs: readonly { userId: string }[]) => {
            if (subs.length === 0) return;
            const currentMap = this.#userDisplayNames.value;
            const unknownIds = subs
                .map((s) => s.userId)
                .filter((id) => !currentMap.has(id))
                .values()
                .unique()
                .toArray();
            if (unknownIds.length === 0) return;
            getBulkUsers(unknownIds)
                .then((users) => {
                    const newMap = new Map(this.#userDisplayNames.value);
                    for (const user of users) {
                        const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
                        newMap.set(user.id, name ? `${name} (${user.email})` : user.email);
                    }
                    this.#userDisplayNames.value = newMap;
                })
                .catch(() => {
                    // Silently fail — display names are best-effort
                });
        };
        const disposeAllSubsEffect = effect(() => resolveUserNames(this.allSubscriptions.value));
        const disposeUserSubsEffect = effect(() => resolveUserNames(this.selectedUserSubscriptions.value));
        this.#disposableStack.defer(disposeAllSubsEffect);
        this.#disposableStack.defer(disposeUserSubsEffect);
    }

    // User search / lookup methods

    setUserSearchValue(value: string): void {
        this.#dispatch(setUserSearchValueAction(value));
    }

    handleUserSelect(item: UserSearchItem): void {
        this.#dispatch(selectUserAction(item.value));
        this.#dispatch(setSelectedUserLabel(item.label));
    }

    handleClearUser(): void {
        this.#dispatch(clearSelectedUser());
        this.#dispatch(setUserSearchValueAction(''));
        this.#dispatch(setSelectedUserLabel(null));
    }

    selectUser(userId: string): void {
        this.#dispatch(selectUserAction(userId));
    }

    clearSelectedUser(): void {
        this.#dispatch(clearSelectedUser());
    }

    // Plan edit/create methods

    editPlan(planId: string): void {
        const plan = this.plans.value.find((p) => p.id === planId);
        if (!plan) return;
        this.#dispatch(clearError());
        this.#dispatch(setMigrationSummary(null));
        this.#dispatch(startEditingPlan(planId, planToFormState(plan)));
    }

    initNewPlan(): void {
        this.#dispatch(clearError());
        this.#dispatch(setMigrationSummary(null));
        this.#dispatch(startCreatingPlan(createDefaultFormState()));
    }

    clearEditPlan(): void {
        this.#dispatch(stopEditingPlan());
    }

    updatePlanFormField<K extends keyof PlanFormState>(key: K, value: PlanFormState[K]): void {
        this.#dispatch(updatePlanFormFieldAction(key, value));
    }

    clearMigrationSummary(): void {
        this.#dispatch(setMigrationSummary(null));
    }

    async savePlan(): Promise<boolean> {
        const plan = this.editingPlan.value;
        const formState = this.#state.value.planFormState;
        if (!plan || !formState) return false;

        this.#dispatch(setPlanSaving(true));
        this.#dispatch(clearError());
        try {
            // Build the request inside the guarded path — Period.parse / Number
            // can throw on malformed form input, and those failures should land
            // in setError() instead of escaping as uncaught save errors.
            const request = formStateToReplaceRequest(formState, plan);
            const result = await this.#replacePlanMutation.mutate({ planId: plan.id, request });
            // Keep only the aggregate counts in state — the per-subscription `results`
            // array can be large on plans with many subscribers and isn't read by the UI.
            const { results: _results, ...counts } = result.migration;
            this.#dispatch(setMigrationSummary(counts));
            invalidatePlansQuery(this.queryClient);
            // Replace can mutate subscriber records (when existingSubscriptionBehavior is
            // 'period_end' or 'immediate_prorated') and updates the denormalized plan
            // snapshot on every subscription to this plan, so the subscriptions cache
            // can be stale even when behavior is 'none'.
            await this.#invalidateAfterSubscriptionChange();
            return true;
        } catch (e) {
            this.#dispatch(setError(e instanceof Error ? e.message : 'Failed to save plan'));
            return false;
        } finally {
            this.#dispatch(setPlanSaving(false));
        }
    }

    async createPlan(): Promise<boolean> {
        const formState = this.#state.value.planFormState;
        if (!formState) return false;

        this.#dispatch(setPlanSaving(true));
        this.#dispatch(clearError());
        try {
            const request = formStateToWriteRequest(formState);
            await this.#createPlanMutation.mutate(request);
            invalidatePlansQuery(this.queryClient);
            return true;
        } catch (e) {
            this.#dispatch(setError(e instanceof Error ? e.message : 'Failed to create plan'));
            return false;
        } finally {
            this.#dispatch(setPlanSaving(false));
        }
    }

    addRole(role: string): void {
        const current = this.#state.value.planFormState?.roles ?? [];
        this.#dispatch(updatePlanFormFieldAction('roles', [...current, role]));
    }

    removeRole(role: string): void {
        const current = this.#state.value.planFormState?.roles ?? [];
        this.#dispatch(
            updatePlanFormFieldAction(
                'roles',
                current.filter((r) => r !== role),
            ),
        );
    }

    addGroup(groupId: string, permission: PermissionType | null): void {
        const current = this.#state.value.planFormState?.groups ?? [];
        if (current.some((g) => g.groupId === groupId)) return;
        this.#dispatch(updatePlanFormFieldAction('groups', [...current, { groupId, permission }]));
    }

    removeGroup(groupId: string): void {
        const current = this.#state.value.planFormState?.groups ?? [];
        this.#dispatch(
            updatePlanFormFieldAction(
                'groups',
                current.filter((g) => g.groupId !== groupId),
            ),
        );
    }

    updateGroupPermission(groupId: string, permission: PermissionType | null): void {
        const current = this.#state.value.planFormState?.groups ?? [];
        this.#dispatch(
            updatePlanFormFieldAction(
                'groups',
                current.map((g) => (g.groupId === groupId ? { ...g, permission } : g)),
            ),
        );
    }

    // Subscription mutations

    async cancelSubscription(subId: string, immediately?: boolean): Promise<void> {
        this.#dispatch(clearError());
        try {
            await this.#cancelSubscriptionMutation.mutate({ id: subId, immediately });
            await this.#invalidateAfterSubscriptionChange();
        } catch (e) {
            this.#dispatch(setError(e instanceof Error ? e.message : 'Failed to cancel subscription'));
            throw e;
        }
    }

    async reactivateSubscription(subId: string): Promise<void> {
        this.#dispatch(clearError());
        try {
            await this.#reactivateSubscriptionMutation.mutate(subId);
            await this.#invalidateAfterSubscriptionChange();
        } catch (e) {
            this.#dispatch(setError(e instanceof Error ? e.message : 'Failed to reactivate subscription'));
            throw e;
        }
    }

    async changeSubscriptionPlan(subId: string, planId: string, immediate: boolean): Promise<void> {
        this.#dispatch(clearError());
        try {
            const request: ChangeSubscriptionRequest = immediate
                ? { immediateNewPlanId: planId }
                : { scheduledNewPlanId: planId };
            await this.#changeSubscriptionMutation.mutate({ id: subId, request });
            await this.#invalidateAfterSubscriptionChange();
        } catch (e) {
            this.#dispatch(setError(e instanceof Error ? e.message : 'Failed to change subscription plan'));
            throw e;
        }
    }

    async refreshPlans(): Promise<void> {
        this.#dispatch(clearError());
        const result = await this.#plansQuery.queryObserver.refetch();
        if (result.isError) {
            this.#dispatch(setError(result.error instanceof Error ? result.error.message : 'Failed to load plans'));
        }
    }

    async #invalidateAfterSubscriptionChange(): Promise<void> {
        invalidateSubscriptionsQuery(this.queryClient);
        const userId = this.#state.value.selectedUserId;
        if (userId) {
            invalidateUserSubscriptionsQuery(this.queryClient, userId);
        }
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
